import React from "react";
import { NavLink } from "react-router-dom";
import { Home, LineChart, Activity, User, ShieldCheck } from "lucide-react";
import { useUnread } from "@/context/UnreadContext";
import { useAdminPending } from "@/context/AdminPendingContext";

const ITEMS = [
  { to: "/",            label: "Home",        icon: Home,       testId: "bnav-home",        end: true,  unreadKey: "announcement" },
  { to: "/education",   label: "Education",   icon: LineChart,  testId: "bnav-education" },
  { to: "/performance", label: "Performance", icon: Activity,   testId: "bnav-performance", unreadKey: "signal" },
  { to: "/profile",     label: "Profile",     icon: User,       testId: "bnav-profile", unreadKey: "dm" },
];

function Item({ item, unreadCount }) {
  return (
    <NavLink
      to={item.to}
      end={!!item.end}
      data-testid={item.testId}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-1 flex-1 py-2 min-h-full ${
          isActive ? "text-[#00b4ff]" : "text-slate-500"
        }`
      }
      style={{ transition: "color 0.15s ease" }}
    >
      {({ isActive }) => (
        <>
          <div
            className={`relative grid place-items-center rounded-lg ${isActive ? "bg-[#00b4ff]/10" : ""}`}
            style={{ width: 32, height: 32, transition: "background-color 0.15s ease" }}
          >
            <item.icon size={18} />
            {unreadCount > 0 && (
              <span
                data-testid={`${item.testId}-badge`}
                className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-mono-plex font-bold grid place-items-center border-2 border-white"
              >
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </div>
          <span className="text-[10px] font-display font-semibold tracking-tight">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

/**
 * Persistent bottom navigation for mobile / tablet — hidden on lg+ where the
 * left sidebar already covers navigation. Includes a safe-area inset padding
 * so it sits above the iOS home indicator when installed as a PWA.
 * Renders unread badges from UnreadContext for Home + Performance.
 */
export default function BottomNav({ isAdmin }) {
  const { counts } = useUnread();
  const { counts: adminCounts } = useAdminPending();
  const adminPending = adminCounts.total || 0;
  return (
    <nav
      data-testid="bottom-nav"
      className="lg:hidden fixed left-0 right-0 bottom-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 flex items-stretch"
      style={{
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        // Ensure the nav is glued to the visible viewport on iOS (avoids
        // the browser-URL-bar collapsing pushing it off-screen).
        transform: "translateZ(0)",
        WebkitBackfaceVisibility: "hidden",
      }}
      aria-label="Primary"
    >
      {ITEMS.map((item) => (
        <Item key={item.to} item={item} unreadCount={item.unreadKey ? counts[item.unreadKey] || 0 : 0} />
      ))}
      {isAdmin && (
        <NavLink
          to="/admin"
          data-testid="bnav-admin"
          className={({ isActive }) =>
            `flex flex-col items-center justify-center gap-1 flex-1 py-2 ${
              isActive ? "text-slate-900" : "text-slate-500"
            }`
          }
          style={{ transition: "color 0.15s ease" }}
        >
          {({ isActive }) => (
            <>
              <div
                className={`relative grid place-items-center rounded-lg ${isActive ? "bg-slate-900 text-white" : ""}`}
                style={{ width: 32, height: 32, transition: "background-color 0.15s ease" }}
              >
                <ShieldCheck size={18} />
                {adminPending > 0 && (
                  <span
                    data-testid="bnav-admin-badge"
                    className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-mono-plex font-bold grid place-items-center border-2 border-white"
                  >
                    {adminPending > 9 ? "9+" : adminPending}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-display font-semibold tracking-tight">Admin</span>
            </>
          )}
        </NavLink>
      )}
    </nav>
  );
}
